/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */
import { useContext, useEffect } from "react";
import { useCotizador } from "../context/apiContext";
import { 
    createFieldErrorStructure,
    getDiffDays,
    getEmptyRequiredFields,
    hasActiveErrors,
    setErrorMessage,
    validateBirthday,
    validateEmailFormat,
    validatePersonName 
} from "../helpers/toolsHelper";
import useFormValidation from "./cotizador/useFormValidation";
import useFormValidateSteps from "./cotizador/useFormValidateSteps";
import useFormValidateDependent from "./cotizador/useFormValidateDependent";
import useFormBenefitValidation from "./cotizador/useFormBenefitValidation";
import { useSteps } from "./useSteps";
import { TextsContext } from "context/textsContext";
import { ERROR_TYPES, MAIN_APPLICANT_FIELDS, VALIDATION_CONSTRAINTS } from "constants/validationConstants";


export const useValidation = () => {
    const { texts } = useContext(TextsContext);
    const { errors, contextState, setErrors, dates } = useCotizador();
    const { getCurrentStepData } = useSteps();
    
    const { validateBirthdayAge, validateStepSpecificRules } = useFormValidation();

    const { validateMainApplicantData, validateDependentData } = useFormValidateSteps();
    const { validateDependantValues } = useFormValidateDependent();
    const { validateBenefits } = useFormBenefitValidation();

    const validateMainApplicantFormValues = () => {

        const validationErrors = {};
        const requiredFieldMessage = texts?.general_field_validation_required ?? 'This field is required';
        const nameValidationMessage = texts?.general_field_validation_name ?? 'Name should not contain numbers';
        const lastNameValidationMessage = texts?.general_field_validation_last_name ?? 'Last name should not contain numbers';
        const emailValidationMessage = texts?.general_field_validation_email ?? 'Please introduce valid email';
        const coverageValidationMessage = texts?.general_field_coverage ?? 
          'Coverage must start either 15 before or 45 days after today';

        MAIN_APPLICANT_FIELDS.forEach(fieldKey => {
            const fieldErrors = createFieldErrorStructure(errors, fieldKey);
            Object.assign(validationErrors, fieldErrors);
        });

        const emptyFields = getEmptyRequiredFields(contextState, MAIN_APPLICANT_FIELDS);
        
        MAIN_APPLICANT_FIELDS.forEach(fieldKey => {
            const isFieldEmpty = emptyFields.includes(fieldKey);
            setErrorMessage(
                validationErrors[fieldKey],
                ERROR_TYPES.EMPTY,
                isFieldEmpty ? requiredFieldMessage : null
            );
        });

        setErrorMessage(
            validationErrors.name,
            ERROR_TYPES.INVALID,
            validatePersonName(contextState.name) ? null : nameValidationMessage
        );

        setErrorMessage(
            validationErrors.surname,
            ERROR_TYPES.INVALID,
            validatePersonName(contextState.surname) ? null : lastNameValidationMessage
        );

        setErrorMessage(
            validationErrors.email,
            ERROR_TYPES.INVALID,
            validateEmailFormat(contextState.email) ? null : emailValidationMessage
        );

        setErrorMessage(
            validationErrors.birthday,
            ERROR_TYPES.INVALID,
            validateBirthdayAge(contextState.birthday, contextState.coverage)
        );

        if (contextState.coverage) {
            const daysDifference = getDiffDays(new Date(), dates.coverage);
            const isDateInValidRange = daysDifference <= VALIDATION_CONSTRAINTS.COVERAGE_DATE_RANGE.MAX_DAYS_AFTER &&
                daysDifference >= VALIDATION_CONSTRAINTS.COVERAGE_DATE_RANGE.MIN_DAYS_BEFORE;
            
            setErrorMessage(
                validationErrors.coverage,
                ERROR_TYPES.INVALID,
                isDateInValidRange ? null : coverageValidationMessage
            );
        }

        const combinedErrors = {
            ...errors,
            ...validationErrors,
            ...validateStepSpecificRules(),
            ...validateBenefits(),
        };

        setErrors(combinedErrors);

        return hasActiveErrors({
            ...errors,
            ...validationErrors
        });
    }

    useEffect(() => {
        const currentStep = getCurrentStepData?.()?.step;
        let stepValidationErrors = {};

        if (currentStep === 1) stepValidationErrors = { ...validateMainApplicantData() };
        if (currentStep === 3) stepValidationErrors = { ...validateDependentData() };

        const combinedErrors = {
            ...errors,
            ...stepValidationErrors,
            ...validateStepSpecificRules(true),
            ...validateBenefits(true)
        };

        setErrors(combinedErrors);

    }, [contextState])

    useEffect(() => {
        if (validateBirthday(contextState.birthday)) {
            return
        }

        const birthdayErrorMessage = texts?.general_field_validation_birthday ?? 
            "Birthday shouldn't be in the future";

        setErrors({
            ...errors,
            birthday: { 
                ...(errors.birthday ?? {}), 
                [ERROR_TYPES.BAD_ENTRY]: birthdayErrorMessage 
            }
        });
        const timeoutId = setTimeout(() => {
            setErrors(prevErrors => ({
                ...prevErrors,
                birthday: { 
                    ...(prevErrors.birthday ?? {}), 
                    [ERROR_TYPES.BAD_ENTRY]: null 
                }
            }));
        }, 5000);

        return () => clearTimeout(timeoutId);

    }, [contextState.birthday]);

    return {
        validateValues: validateMainApplicantFormValues,
        validateDependantValues,
    }
}